// TAUKE POS — SPA entry
// see architecture.md#L580 (前端架構)

import './components/pos-app.js';
import './components/pos-auth-gate.js';
import './components/pos-nav.js';
import './components/pos-login.js';
import './components/pos-tenant-setup.js';
import './components/pos-customer-menu.js';
import './components/pos-customer-checkout.js';
import './components/pos-customer-reservation.js';
import './components/pos-customer-reservation-manage.js';
import './components/pos-reservations.js';
import './components/pos-reservation-config.js';
import './components/pos-dashboard.js';
import './components/pos-order-grid.js';
import './components/pos-order-list.js';
import './components/pos-menu-grid.js';
import './components/pos-menu-editor.js';
import './components/pos-staff-mgr.js';
import './components/pos-settings.js';
import './components/pos-menu-item.js';
import './components/pos-cart.js';
import './components/pos-checkout.js';
import './components/pos-checkout-result.js';
import './components/pos-payment-settings.js';
import './components/pos-xiaohong-chat.js';
import './components/pos-xiaonuan-reviewer.js';
import './components/pos-ai-keys-admin.js';
import './components/pos-lighthouse-redemption.js';
import './components/pos-qr-settings.js';
import './components/pos-stations-settings.js';
import './components/pos-inventory.js';
import './components/pos-xiaocang-alerts.js';
import './components/pos-reports.js';
import './components/pos-workflow-editor.js';
import './components/pos-scheduling.js';
import './components/pos-scheduling-settings.js';
import './components/pos-my-shifts.js';
import './components/pos-onboarding-checklist.js';
import './components/pos-mascot-intro.js';

import { initRouter } from './lib/router.js';
import { getSupabase } from './lib/supabase-client.js';

// DOMContentLoaded 之前 Web Components 已 upgrade
window.addEventListener('DOMContentLoaded', async () => {
  const supabase = getSupabase();
  // Debug helper: DEV only. In prod we don't expose the client so XSS can't
  // piggyback on `window.__supabase.auth.signInWithPassword(...)` etc.
  // Anon key is still in the bundle, but at least attackers need to construct
  // their own client, which is a minor friction.
  if (import.meta.env.DEV) {
    window.__supabase = supabase;
  }

  // 如果 URL 有 magic link 帶回的 auth 參數（?code= 或 #access_token=），
  // 先等 supabase-js 處理完，避免 router mount 時洗掉 URL fragment。
  const AUTH_HASH_PARAMS = ['access_token', 'refresh_token', 'expires_at', 'expires_in', 'token_type', 'provider_token', 'type', 'error', 'error_code', 'error_description'];
  const AUTH_QUERY_PARAMS = ['code', 'state', 'error', 'error_description'];
  const hasAuthInUrl = /(access_token|refresh_token|error_description)=/.test(location.hash)
    || /[?&]code=/.test(location.search);

  if (hasAuthInUrl) {
    // Codex W1 重要 #8：timeout 路徑也必須 unsubscribe，避免 listener 殘留
    await new Promise((resolve) => {
      let sub;
      const done = () => {
        try { sub?.subscription?.unsubscribe(); } catch { /* noop */ }
        resolve();
      };
      const timer = setTimeout(done, 3000);
      sub = supabase.auth.onAuthStateChange((event) => {
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
          clearTimeout(timer);
          done();
        }
      }).data;
    });

    // Codex W1 重要 #8：只清 supabase auth 相關 param，不洗掉用戶自己的 query/hash
    cleanAuthParamsFromUrl(AUTH_HASH_PARAMS, AUTH_QUERY_PARAMS);
  }

  const { data } = await supabase.auth.getSession();
  applySessionToBody(data?.session);

  // 後續 session 變化（token refresh / sign-in / sign-out）都同步到 body dataset，
  // 讓 router guard 讀得到最新 role
  supabase.auth.onAuthStateChange((_e, session) => applySessionToBody(session));

  initRouter();
});

// 只從 hash / search 移除指定 auth 相關 key，保留其他使用者狀態
function cleanAuthParamsFromUrl(hashKeys, queryKeys) {
  let changed = false;

  // 處理 search (?...)
  if (location.search) {
    const sp = new URLSearchParams(location.search);
    let touched = false;
    for (const k of queryKeys) if (sp.has(k)) { sp.delete(k); touched = true; }
    if (touched) {
      const newSearch = sp.toString();
      history.replaceState({}, '', location.pathname + (newSearch ? '?' + newSearch : '') + location.hash);
      changed = true;
    }
  }

  // 處理 hash (#...)
  // magic link 的 hash 格式是 #access_token=x&refresh_token=y&...（key=value 對），
  // 但我們也用 hash-based router 的 `#/route` —— 不能誤清
  if (location.hash && location.hash.includes('=')) {
    const raw = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
    // 只處理「像 query string」的 hash（含 `=`），保留 `#/route` 形式
    if (!raw.startsWith('/')) {
      const hp = new URLSearchParams(raw);
      let touched = false;
      for (const k of hashKeys) if (hp.has(k)) { hp.delete(k); touched = true; }
      if (touched) {
        const remaining = hp.toString();
        const newHash = remaining ? '#' + remaining : '';
        history.replaceState({}, '', location.pathname + location.search + newHash);
        changed = true;
      }
    }
  }

  return changed;
}

function applySessionToBody(session) {
  const prevRole = document.body.dataset.role || '';
  const prevTenant = document.body.dataset.tenantId || '';
  const prevAuthed = document.body.dataset.authed || '';

  document.body.dataset.authed = session ? 'true' : 'false';
  if (!session) {
    // Codex W1 重要 #9：sign-out 時清乾淨，不能只清 role 留 tenantId
    document.body.dataset.role = '';
    document.body.dataset.tenantId = '';
  } else {
    try {
      // base64url → base64 + padding（Codex W1 重要 #9）
      let b64 = session.access_token.split('.')[1];
      b64 = b64.replace(/-/g, '+').replace(/_/g, '/');
      const pad = b64.length % 4;
      if (pad === 2) b64 += '==';
      else if (pad === 3) b64 += '=';
      else if (pad === 1) throw new Error('bad base64 length');
      const claims = JSON.parse(atob(b64));
      // 用 user_role（自訂 claim），不讀保留的 role（PostgREST 用的）
      document.body.dataset.role = claims.user_role || '';
      document.body.dataset.tenantId = claims.tenant_id || '';
    } catch {
      document.body.dataset.role = '';
      document.body.dataset.tenantId = '';
    }
  }

  // Codex W1 重要 #2：auth 狀態實質變化才觸發 route re-evaluation
  // 避免 token refresh（claims 不變）也 remount 正在用的 route 元件
  const changed =
    prevAuthed !== document.body.dataset.authed ||
    prevRole !== document.body.dataset.role ||
    prevTenant !== document.body.dataset.tenantId;
  if (changed) {
    window.dispatchEvent(new Event('auth:changed'));
  }
}

// Service Worker 註冊（vite-plugin-pwa 自動生成，這裡只是防禦）
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  navigator.serviceWorker.register('/sw.js').catch((err) => {
    console.error('[sw] register failed', err);
  });
}
